var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
// Mixins
import { UseRippleEffect, rippleEffectStyles, } from '@frontend_zz/web-component-mixins/UseRippleEffect/UseRippleEffect.js';
// Components
import { BaseElement } from '@frontend_zz/base-element/BaseElement.js';
import { Icon } from '@frontend_zz/icon/Icon.js';
// Styles
import styles from './Button.css.js';
// Data
import config from './Button.config.json';
/**
 * @element zz-button
 */
export class Button extends UseRippleEffect(BaseElement) {
    constructor() {
        super(...arguments);
        this.size = 'regular';
        this.variant = 'primary';
        this.iconSize = 'm';
        this.iconPosition = 'left';
        this.screenReaderLabel = false;
        this.hideLabelOnMobile = false;
        this.ariaLabel = '';
        this.theme = config.defaultThemeName;
        /* <button> properties */
        this.disabled = false;
        this.type = 'button';
        /* <a> properties */
        this.rel = '';
        this.target = '_self';
        this.inline = false;
        this.buttonRef = createRef();
    }
    static get styles() {
        return [styles, rippleEffectStyles];
    }
    static get scopedElements() {
        return {
            'zz-icon': Icon,
        };
    }
    firstUpdated() {
        this.init();
    }
    init() {
        this.bindEvents();
    }
    /**
     * Overload native focus method to redirect focus to the actual button element
     */
    focus(options) {
        const { buttonRef } = this;
        buttonRef.value?.focus(options);
    }
    bindEvents() {
        const button = this.shadowRoot?.querySelector('.button');
        if (button && !['text', 'underline', 'white'].includes(this.variant)) {
            this.attachRippleEffect(button);
        }
        const slot = this.shadowRoot?.querySelector('slot');
        slot.addEventListener('slotchange', () => {
            this.updateEmpty();
        });
        this.updateEmpty();
    }
    updateEmpty() {
        const slot = this.shadowRoot?.querySelector('slot');
        if (slot.assignedNodes().length === 0) {
            this.setAttribute('empty', '');
        }
        else {
            this.removeAttribute('empty');
        }
    }
    render() {
        const { icon, iconSize, disabled, buttonRef } = this;
        const content = [
            this.icon
                ? html `
                      <zz-icon
                          class="icon"
                          type="${icon}"
                          size=${ifDefined(iconSize)}
                          ?disabled="${disabled}"
                      ></zz-icon>
                  `
                : nothing,
            html `
                <span class="label">
                    <slot></slot>
                </span>
            `,
        ];
        const onAnchorClick = (event) => {
            this.dispatchEvent(new CustomEvent('universalAnchorClick', {
                composed: true,
                bubbles: true,
                cancelable: true,
                detail: {
                    href: this.href,
                    nativeEvent: event,
                },
            }));
        };
        if (this.href && this.href.length > 0) {
            return html `
                <a class="button" href="${this.href}" rel=${this.rel} target="${this.target}" @click=${onAnchorClick}
                    >${content}</a
                >
            `;
        }
        return html `
            <button
                ${ref(buttonRef)}
                aria-label=${ifDefined(this.ariaLabel)}
                class="button"
                .type="${this.type}"
                ?disabled="${this.disabled}"
            >
                ${content}
            </button>
        `;
    }
}
__decorate([
    property({ type: String, reflect: true })
], Button.prototype, "size", void 0);
__decorate([
    property({ type: String, reflect: true })
], Button.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "icon", void 0);
__decorate([
    property({ type: String, attribute: 'icon-size', reflect: true })
], Button.prototype, "iconSize", void 0);
__decorate([
    property({ type: String, attribute: 'icon-position', reflect: true })
], Button.prototype, "iconPosition", void 0);
__decorate([
    property({ type: Boolean, attribute: 'screenreader-label', reflect: true })
], Button.prototype, "screenReaderLabel", void 0);
__decorate([
    property({ type: Boolean, attribute: 'hide-label-on-mobile', reflect: true })
], Button.prototype, "hideLabelOnMobile", void 0);
__decorate([
    property({ type: String, attribute: 'aria-label' })
], Button.prototype, "ariaLabel", void 0);
__decorate([
    property({ reflect: true })
], Button.prototype, "theme", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], Button.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "type", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "rel", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "href", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "target", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], Button.prototype, "inline", void 0);
